import { Country } from 'country-state-city';
import {  onAuthStateChanged } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LocationSelector from '../../components/common/Forms/LocationSelector';
import { academyAPI, authAPI, profileAPI } from '../../services/api';
import './Academy.css';
import { errorHandler } from '../../utils/errorHandler';
import { auth, db } from '../../services/firebase';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { academyAppCache } from '../../utils/cache';
import { COUNTRIES_WITH_STATES } from '../../utils/locationUtils';
import { useAuth } from '../../contexts/AuthContext';

function Academy() {
    const navigate = useNavigate();
    const { reloadUser } = useAuth();
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
        organization: '',
        educationLevel: '',
        institution: '',
        fieldOfStudy: '',
        graduationYear: '',
        linkedIn: '',
        socialMedia: '',
        portfolio: '',
        experience: '',
        motivation: '',
        resume: null,
        heardFrom: '',
        country: '',
        state: '',
        city: '',
        profileImage: ''
    });

    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [existingApplication, setExistingApplication] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasApplied, setHasApplied] = useState(false);
    const [applicationStatus, setApplicationStatus] = useState('pending');
    const [userProfile, setUserProfile] = useState(null);

    const fetchUserProfile = async (authUser) => {
        try {
            const profileData = await profileAPI.getProfile();
            setUserProfile(profileData);
            
            setFormData(prevData => ({
                ...prevData,
                firstName: profileData.firstName || '',
                lastName: profileData.lastName || '',
                email: authUser.email || '',
                phone: profileData.phoneNumber || '',
                profileImage: authUser.photoURL || profileData.profileImageUrl || ''
            }));
        } catch (err) {
            setFormData(prevData => ({
                ...prevData,
                email: authUser.email || '',
                profileImage: authUser.photoURL || '',
            }));
        }
    };

    const fetchApplicationStatus = async (authUser) => {
        try {
            setIsLoading(true);
            setError(null);
            
            academyAppCache.delete('status');

            const idTokenResult = await authUser.getIdTokenResult(true);
            const hasAppliedClaim = idTokenResult.claims.hasAppliedToAcademy || false;

            if (hasAppliedClaim) {
                try {
                    const response = await academyAPI.getApplication();
                    
                    if (response && response.id) {
                        academyAppCache.set('status', { ...response, status: response.status, hasApplied: true });
                        
                        setHasApplied(true);
                        setApplicationStatus(response.status || 'pending');
                        setExistingApplication(response);
                    } else {
                        console.warn('Claim indicated user has applied, but failed to fetch application details.');
                        setHasApplied(true);
                        setApplicationStatus('unknown');
                        setExistingApplication(null);
                    }
                } catch (appError) {
                    setError({
                        message: 'We\'ve seen you\'ve applied, but couldn\'t retrieve your application details. Please try again later.',
                        originalError: appError
                    });
                    setHasApplied(true);
                    setApplicationStatus('error_fetching');
                }
            } else {
                const data = {
                    hasApplied: false,
                    applicationStatus: null
                };
                academyAppCache.set('status', data);
                setHasApplied(false);
                setApplicationStatus('pending');
                setExistingApplication(null);
            }
        } catch (error) {
            const processedError = errorHandler.processError(error);
            setError(processedError);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                navigate('/sign-in');
                return;
            }

            await fetchUserProfile(user);
            await fetchApplicationStatus(user);
        });

        return () => unsubscribe();
    }, [navigate]);

    useEffect(() => {
        return () => {
            academyAppCache.delete('status');
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'email') return;
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setMessage('File size must be less than 5MB');
                return;
            }
            if (!file.type.includes('pdf') && !file.type.includes('doc') && !file.type.includes('docx')) {
                setMessage('Please upload a PDF or Word document');
                return;
            }
            setFormData(prev => ({
                ...prev,
                resume: file
            }));
        }
    };

    const handleSubmitApplication = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);

            const baseRequiredFields = {
                firstName: 'First Name',
                lastName: 'Last Name',
                email: 'Email',
                phone: 'Phone Number',
                educationLevel: 'Education Level',
                experience: 'Experience',
                motivation: 'Motivation',
                country: 'Country',
                city: 'City',
                resume: 'Resume',
                fieldOfStudy: 'Field of Study',
                graduationYear: 'Graduation Year',
                institution: 'Institution'
            };
            
            const requiredFields = {...baseRequiredFields};
            
            if (COUNTRIES_WITH_STATES && COUNTRIES_WITH_STATES.includes(formData.country)) {
                requiredFields.state = 'State/Province';
            }

            const missingFields = Object.entries(requiredFields)
                .filter(([field]) => {
                    if (field === 'motivation') {
                        const isMissing = !formData[field] || formData[field].trim() === '';
                        return isMissing;
                    }
                    return !formData[field];
                })
                .map(([_, label]) => label);
                
            if (missingFields.length > 0) {
                setError({
                    message: `Please fill in the following required fields: ${missingFields.join(', ')}`,
                    code: 'VALIDATION_ERROR'
                });
                
                const firstMissingField = Object.entries(requiredFields)
                    .find(([field]) => {
                        if (field === 'motivation') {
                            return !formData[field] || formData[field].trim() === '';
                        }
                        return !formData[field];
                    });
                
                if (firstMissingField) {
                    const fieldId = firstMissingField[0];
                    const element = document.getElementById(fieldId);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        element.classList.add('highlight-field');
                        setTimeout(() => {
                            element.classList.remove('highlight-field');
                        }, 3000);
                        element.focus();
                    }
                }
                
                setLoading(false);
                return;
            }

            let resumeData = formData.resume;
            if (formData.resume instanceof File) {
                const reader = new FileReader();
                resumeData = await new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(formData.resume);
                });
            }

            const applicationData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                dateOfBirth: formData.dateOfBirth || '',
                gender: formData.gender || '',
                organization: formData.organization || '',
                educationLevel: formData.educationLevel,
                institution: formData.institution,
                fieldOfStudy: formData.fieldOfStudy,
                graduationYear: formData.graduationYear,
                linkedIn: formData.linkedIn || '',
                socialMedia: formData.socialMedia || '',
                portfolio: formData.portfolio || '',
                experience: formData.experience,
                motivation: formData.motivation,
                resume: resumeData,
                heardFrom: formData.heardFrom || '',
                country: formData.country,
                state: formData.state,
                city: formData.city,
                profileImage: formData.profileImage || auth.currentUser.photoURL || ''
            };

            const response = await academyAPI.submitApplication(applicationData);
            
            if (response && response.id) {
                setMessage('Application submitted successfully!');
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    dateOfBirth: '',
                    gender: '',
                    organization: '',
                    educationLevel: '',
                    institution: '',
                    fieldOfStudy: '',
                    graduationYear: '',
                    linkedIn: '',
                    socialMedia: '',
                    portfolio: '',
                    experience: '',
                    motivation: '',
                    resume: null,
                    heardFrom: '',
                    country: '',
                    state: '',
                    city: '',
                    profileImage: ''
                });
                setShowForm(false);
                
                if (reloadUser) {
                    await reloadUser();
                }
                await fetchApplicationStatus(auth.currentUser);
            } else {
                throw new Error('Failed to submit application. The server responded but the operation may not have completed successfully.');
            }
        } catch (error) {
            setError(errorHandler.processError(error));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateApplicationStatus = async (applicationId, newStatus) => {
        try {
            setLoading(true);
            await academyAPI.updateApplicationStatus(applicationId, newStatus);
        } catch (error) {
            setError({
                message: 'Failed to update application status. Please try again.',
                code: 'UPDATE_ERROR'
            });
        } finally {
            setLoading(false);
        }
    };

    const renderApplicationStatus = () => {
        if (!existingApplication) return null;

        const formatDate = (dateString) => {
            if (!dateString) return 'Not submitted yet';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Not submitted yet';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        const getStatusIcon = (status) => {
            switch (status) {
                case 'pending':
                    return '⏳';
                case 'approved':
                    return '✅';
                case 'rejected':
                    return '🙁';
                case 'waitlist':
                    return '📋';
                default:
                    return '⏳';
            }
        };

        const status = existingApplication.status || 'pending';
        const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1);

        return (
            <div className="application-status">
                <div className="status-card">
                    <div className="status-header">
                        <div className="applicant-profile">
                            <div className="academy-profile-image">
                                {existingApplication.profileImage ? (
                                    <img 
                                        src={existingApplication.profileImage} 
                                        alt={`${existingApplication.firstName || ''} ${existingApplication.lastName || ''}`}
                                    />
                                ) : (
                                    <div className="profile-placeholder">
                                        {(existingApplication.firstName?.[0] || '') + (existingApplication.lastName?.[0] || '')}
                                    </div>
                                )}
                            </div>
                            <div className="profile-info">
                                <h1>{existingApplication.firstName || ''} {existingApplication.lastName || ''}</h1>
                                <p className="submission-info">
                                    {existingApplication.createdAt ? 'Application submitted on ' : ''}{formatDate(existingApplication.createdAt)}
                                </p>
                            </div>
                        </div>
                        <div className={`status-indicator status-${status}`}>
                            <span className="status-icon">{getStatusIcon(status)}</span>
                            <span className="status-text">
                                {formattedStatus}
                                {existingApplication.updatedAt && (
                                    <span className="update-time">
                                        (Updated: {formatDate(existingApplication.updatedAt)})
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="application-sections">
                        <div className="section personal-info">
                            <h2>Personal Information</h2>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Email</label>
                                    <span>{existingApplication.email}</span>
                                </div>
                                <div className="info-item">
                                    <label>Phone</label>
                                    <span>{existingApplication.phone}</span>
                                </div>
                                <div className="info-item">
                                    <label>Location</label>
                                    <span>
                                        {existingApplication.city}
                                        {existingApplication.state && `, ${existingApplication.state}`}
                                        {existingApplication.country && `, ${Country.getCountryByCode(existingApplication.country)?.name}`}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Date of Birth</label>
                                    <span>{existingApplication.dateOfBirth}</span>
                                </div>
                            </div>
                        </div>

                        <div className="section education">
                            <h2>Educational Background</h2>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Education Level</label>
                                    <span>{existingApplication.educationLevel}</span>
                                </div>
                                <div className="info-item">
                                    <label>Institution</label>
                                    <span>{existingApplication.institution}</span>
                                </div>
                                <div className="info-item">
                                    <label>Field of Study</label>
                                    <span>{existingApplication.fieldOfStudy}</span>
                                </div>
                                <div className="info-item">
                                    <label>Graduation Year</label>
                                    <span>{existingApplication.graduationYear}</span>
                                </div>
                            </div>
                        </div>

                        <div className="section experience">
                            <h2>Experience & Motivation</h2>
                            <div className="text-content">
                                <div className="info-item">
                                    <label>Relevant Experience</label>
                                    <p>{existingApplication.experience}</p>
                                </div>
                                <div className="info-item">
                                    <label>Motivation Letter</label>
                                    <p>{existingApplication.motivation}</p>
                                </div>
                            </div>
                        </div>

                        <div className="section documents">
                            <h2>Submitted Documents</h2>
                            <div className="document-links">
                                {existingApplication.resume && (
                                    <a 
                                        href={existingApplication.resume}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="document-link"
                                    >
                                        <span className="document-icon">📄</span>
                                        <span>View Resume</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                    <p className="contact-info">
                        Please contact us at fomeje@gmail.com if there are any modifications needed in the application.
                    </p>
                </div>
            </div>
        );
    };

    const renderError = () => {
        if (!error) return null;
        
        const errorMessage = typeof error === 'object' ? error.message : error;
        
        return (
            <div className="error-message">
                <div className="message error">
                    <p>{errorMessage}</p>
                    <button 
                        onClick={() => setError(null)}
                        className="retry-button"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="academy-error-container">
                <div className="back-to-projects">
                    <Link to="/projects">← Back to Projects</Link>
                </div>
                
                <div className="error-container">
                    <h2>Error</h2>
                    <p>{typeof error === 'object' ? error.message : error}</p>
                    <button onClick={() => setError(null)}>Try Again</button>
                </div>
            </div>
        );
    }

    if (hasApplied) {
        return renderApplicationStatus();
    }

    return (
        <div className="academy-container">
            <div className="back-to-projects">
                <Link to="/projects">← Back to Projects</Link>
            </div>

            <div className="academy-header">
                <div className="page-header-content">
                    <h1>The Mediterranean Academy For Youth - 2025</h1>
                    <div className="header-divider"></div>
                    <p className="header-description">
                        Join our prestigious academy program designed for ambitious Mediterranean youth leaders.
                    </p>
                </div>
            </div>

            <div className="title-container">
                <h1>Apply to Mediterranean Academy For Youth - 2025</h1>
            </div>
            
            <form onSubmit={handleSubmitApplication} className="academy-form">
                {error && <div className="message error">{typeof error === 'object' ? error.message : error}</div>}
                {message && <div className="message">{message}</div>}
                
                <div className="form-section">
                    <h2>Personal Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="firstName">First Name *</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="lastName">Last Name *</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="email">Email *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                disabled={true}
                                className="input-disabled"
                                required
                            />
                            <small className="field-note">Email is linked to your account and cannot be changed</small>
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="dateOfBirth">Date of Birth *</label>
                            <input
                                type="date"
                                id="dateOfBirth"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="gender">Gender</label>
                            <select
                                id="gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="prefer-not-to-say">Prefer not to say</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Location *</label>
                            <LocationSelector
                                countryCode={formData.country}
                                stateCode={formData.state}
                                city={formData.city}
                                onCountryChange={(code) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        country: code,
                                        state: '',
                                        city: ''
                                    }));
                                }}
                                onStateChange={(code) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        state: code,
                                        city: ''
                                    }));
                                }}
                                onCityChange={(cityName) => {
                                    if (cityName) {
                                        setFormData(prev => ({
                                            ...prev,
                                            city: cityName
                                        }));
                                    }
                                }}
                                required={true}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="organization">Organization</label>
                            <input
                                type="text"
                                id="organization"
                                name="organization"
                                value={formData.organization}
                                onChange={handleInputChange}
                                placeholder="Your current organization/company"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h2>Educational Background</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="educationLevel">Education Level *</label>
                            <select
                                id="educationLevel"
                                name="educationLevel"
                                value={formData.educationLevel}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select Education Level</option>
                                <option value="high-school">High School</option>
                                <option value="bachelors">Bachelor's Degree</option>
                                <option value="masters">Master's Degree</option>
                                <option value="phd">Ph.D.</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="institution">Institution *</label>
                            <input
                                type="text"
                                id="institution"
                                name="institution"
                                value={formData.institution}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="fieldOfStudy">Field of Study *</label>
                            <input
                                type="text"
                                id="fieldOfStudy"
                                name="fieldOfStudy"
                                value={formData.fieldOfStudy}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="graduationYear">Graduation Year *</label>
                            <input
                                type="number"
                                id="graduationYear"
                                name="graduationYear"
                                min="1900"
                                max="2030"
                                value={formData.graduationYear}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h2>Professional Profile</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="linkedIn">LinkedIn Profile</label>
                            <input
                                type="url"
                                id="linkedIn"
                                name="linkedIn"
                                value={formData.linkedIn}
                                onChange={handleInputChange}
                                placeholder="https://linkedin.com/in/yourprofile"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="socialMedia">Social Profile</label>
                            <input
                                type="url"
                                id="socialMedia"
                                name="socialMedia"
                                value={formData.socialMedia}
                                onChange={handleInputChange}
                                placeholder="https://www.facebook.com/yourprofile"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="portfolio">Portfolio Website</label>
                        <input
                            type="url"
                            id="portfolio"
                            name="portfolio"
                            value={formData.portfolio}
                            onChange={handleInputChange}
                            placeholder="https://yourportfolio.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="experience">Relevant Experience *</label>
                        <textarea
                            id="experience"
                            name="experience"
                            value={formData.experience}
                            onChange={handleInputChange}
                            required
                            placeholder="Please describe your relevant experience..."
                            rows="4"
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h2>Additional Information</h2>
                    <div className="form-group">
                        <label htmlFor="motivation">Motivation Letter *</label>
                        <textarea
                            id="motivation"
                            name="motivation"
                            value={formData.motivation}
                            onChange={handleInputChange}
                            placeholder="Tell us why you want to join our academy..."
                            rows="6"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="resume" className="file-label">Resume/CV (PDF or Word) *</label>
                        <div className="file-upload-container">
                            <input
                                type="file"
                                id="resume"
                                name="resume"
                                onChange={handleFileChange}
                                accept=".pdf,.doc,.docx"
                                className="file-input"
                                required
                            />
                            <label htmlFor="resume" className="file-upload-button">
                                <i className="fas fa-upload"></i> Upload Resume
                            </label>
                            {formData.resume && (
                                <div className="file-info">
                                    <span className="file-name">
                                        <i className="fas fa-file-pdf"></i> {formData.resume.name}
                                    </span>
                                    <span className="file-size">
                                        ({(formData.resume.size / 1024 / 1024).toFixed(2)} MB)
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="heardFrom">How did you hear about us?</label>
                        <select
                            id="heardFrom"
                            name="heardFrom"
                            value={formData.heardFrom}
                            onChange={handleInputChange}
                        >
                            <option value="">Select an option</option>
                            <option value="social-media">Social Media</option>
                            <option value="friend">Friend/Family</option>
                            <option value="search">Search Engine</option>
                            <option value="advertisement">Advertisement</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" disabled={loading}>
                        {loading ? 'Submitting...' : 'Submit Application'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default Academy;